import { useMemo } from 'react';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { startPractice, endPractice } from './practiceSession';
import { getPracticeDataset, usePracticeDataset } from './practiceDataset';
import { practiceCreateSubrecipe, practiceUpdateCostPrice } from './practiceGuards';
import { beginCustomerInventoryPracticeData, beginInventoryPracticeData, endInventoryPracticeData, getInventoryPracticeDataset, useInventoryPracticeDataset, selectInventoryPractice, updateInventoryPracticeCount, relinkInventoryPractice, type InventoryPracticeDataset } from './inventoryPracticeData';
export * from './inventoryPracticeData';
type Store = ReturnType<typeof useInventoryStore.getState>;
const noop = async () => { };
const actions: Partial<Store> = {
    loadCountReview: async (_restaurant, selection) => { selectInventoryPractice(selection); },
    loadSessionSummaries: noop, loadSectionProducts: noop, loadCanonicalUnits: noop, loadCountsHistory: noop,
    loadAvailableProducts: noop, loadAvailableCustomIngredients: noop, loadAvailableSubrecipes: noop,
    invalidateCountReview: noop,
    updateCountQuantities: async (countId, _restaurant, items) => updateInventoryPracticeCount(countId, items),
    addCountProducts: async (countId, _restaurant, items) => updateInventoryPracticeCount(countId, items, true),
    relinkSectionProduct: async (productId, payload) => relinkInventoryPractice(productId, payload),
    relinkSectionProductByKey: async (sectionId, payload) => {
        const assignment = getInventoryPracticeDataset()?.sectionProducts[sectionId]?.find(p => p.productKey === payload.oldProductKey);
        if (!assignment)
            throw new Error('Unknown practice assignment');
        relinkInventoryPractice(assignment.id, payload);
    },
};
function view(state: Store, data = getInventoryPracticeDataset(), cost = getPracticeDataset()): Store {
    if (!data)
        return state;
    return {
        ...state, ...data, ...actions, sessionsHasMore: false, isLoadingSessions: false, isLoadingCounts: false, isLoadingAvailable: false,
        countsLoadedForRestaurantId: data.restaurantId, countsCompleteForRestaurantId: data.restaurantId,
        availableCustomIngredients: data.source === 'customer' ? state.availableCustomIngredients : [],
        availableSubrecipes: data.source === 'customer' ? state.availableSubrecipes : (cost?.subrecipesList ?? []).map(s => ({
            productKey: `subrecipe:${s.id}`, subrecipeId: s.id, productName: s.name, portions: s.portions, portionsUnit: s.portionsUnit ?? 'g', stdUnit: s.portionsUnit === 'g' ? 'g' as const : s.portionsUnit === 'ml' ? 'ml' as const : 'p' as const, pricePerStdUnit: 0, assignedToSectionIds: [], assignedToSectionNames: []
        })),
    };
}
export const getInventoryViewState = () => view(useInventoryStore.getState());
/** Same selectors as the store, with a scoped fixture overlay. No real cache is replaced. */
function useView<T = Store>(selector?: (state: Store) => T): T {
    const real = useInventoryStore();
    const data = useInventoryPracticeDataset();
    const cost = usePracticeDataset(!!data);
    const state = useMemo(() => view(real, data, cost), [real, data, cost]);
    return selector ? selector(state) : state as unknown as T;
}
export const useInventoryViewStore = Object.assign(useView, { getState: getInventoryViewState });
let snapshot: Record<string, unknown> | null = null;
let customerSnapshot: { mode: 'inventory' | 'leakage'; data: InventoryPracticeDataset } | null = null;
let selectionToRestore: { restaurantId: string; selection: Store['countReview']['selection'] } | null = null;
let restoreSnapshot: { restaurantId: string; view: Record<string, unknown> } | null = null;
export const inventoryViewToRestore = (restaurantId?: string) => restoreSnapshot && restoreSnapshot.restaurantId === restaurantId ? restoreSnapshot.view : null;
export const clearInventoryViewRestore = () => { restoreSnapshot = null; };
/** Capture before the launch loader normalizes the customer's current filters. */
export function captureInventoryPracticeView() {
    const restaurantId = useAppUserDefaultStore.getState().restaurant?.id;
    if (!restaurantId || snapshot) return;
    snapshot = {};
    selectionToRestore = { restaurantId, selection: { ...useInventoryStore.getState().countReview.selection } };
    window.dispatchEvent(new CustomEvent('onboarding:inventory-snapshot-view', { detail: snapshot }));
}
export function startInventoryPractice(mode: 'inventory' | 'leakage', options: { source: 'customer' | 'fixture' } = { source: 'fixture' }): boolean {
    if (options.source === 'fixture' && !startPractice(true, true))
        return false;
    const restaurantId = useAppUserDefaultStore.getState().restaurant?.id;
    if (!restaurantId)
        return false;
    if (!snapshot) {
        snapshot = {};
        window.dispatchEvent(new CustomEvent('onboarding:inventory-snapshot-view', { detail: snapshot }));
    }
    if (options.source === 'customer') {
        const real = useInventoryStore.getState();
        if (real.currentRestaurantId !== restaurantId || real.countsLoadedForRestaurantId !== restaurantId ||
            real.countReview.isLoading || !real.countReview.loadedKey?.startsWith(`${restaurantId}|`) || !real.countReview.data?.products.length) return false;
        beginCustomerInventoryPracticeData({
            restaurantId, groups: real.groups, sections: real.sections, countsHistory: real.countsHistory,
            sectionProducts: real.sectionProducts, availableProducts: real.availableProducts,
            sessionSummaries: real.sessionSummaries, canonicalUnitsByGroup: real.canonicalUnitsByGroup,
            countReview: { ...real.countReview, data: real.countReview.data, isLoading: false, loadedKey: 'customer-snapshot' },
        });
        customerSnapshot = { mode, data: getInventoryPracticeDataset()! };
        window.dispatchEvent(new Event('onboarding:inventory-reset-view'));
        return true;
    }
    beginInventoryPracticeData(restaurantId);
    // Keep one configured product absent from recipes for the setup exercise.
    const fixture = getPracticeDataset();
    const missingProduct = fixture?.orderHistoryList[0]?.productData[5];
    if (missingProduct)
        for (const dish of fixture?.costPriceList ?? []) {
            if (dish.ingredients.some(i => i.articleId === missingProduct.articleId)) {
                practiceUpdateCostPrice({ id: dish.id, ingredients: dish.ingredients.filter(i => i.articleId !== missingProduct.articleId) });
            }
        }
    // One prep portion per dish keeps the added 100 g ingredient at 100 g per sale.
    // This setup exercise never touches a restaurant prep.
    const product = getPracticeDataset()?.orderHistoryList[0]?.productData[0];
    const subrecipe = product ? practiceCreateSubrecipe({
        restaurantId, name: product.name, portions: 1, portionsUnit: 'p', ingredients: [{
                articleId: product.articleId, productName: product.name, platform: 'any_invoice', quantityNeeded: 100, quantityInfo: product.quantityInfo
            }]
    } as Parameters<typeof practiceCreateSubrecipe>[0]) : undefined;
    const dish = getPracticeDataset()?.costPriceList[0];
    if (subrecipe && dish)
        practiceUpdateCostPrice({ id: dish.id, ingredients: [...dish.ingredients, { subrecipeId: subrecipe.id, subrecipePortionsNeeded: 1 }] });
    window.dispatchEvent(new Event('onboarding:inventory-reset-view'));
    if (mode === 'leakage')
        selectInventoryPractice({ groupId: getInventoryPracticeDataset()!.groups[0].id, sessionId: null });
    return true;
}
/** Cross the exercise boundary in either direction without replacing the saved view. */
export function setInventoryExerciseSource(mode: 'inventory' | 'leakage', exercise: boolean) {
    if (!customerSnapshot || customerSnapshot.mode !== mode) return;
    if (exercise && getInventoryPracticeDataset()?.source === 'customer') startInventoryPractice(mode);
    else if (!exercise && getInventoryPracticeDataset()?.source !== 'customer') {
        window.dispatchEvent(new Event('onboarding:inventory-close-all'));
        window.dispatchEvent(new Event('onboarding:inventory-leakage-close-all'));
        endPractice();
        beginCustomerInventoryPracticeData(customerSnapshot.data);
        window.dispatchEvent(new Event('onboarding:inventory-reset-view'));
    }
}
export function endInventoryPractice(): void {
    if (!getInventoryPracticeDataset() && !snapshot)
        return;
    window.dispatchEvent(new Event('onboarding:inventory-cancel-actions'));
    window.dispatchEvent(new Event('onboarding:inventory-close-all'));
    window.dispatchEvent(new Event('onboarding:inventory-leakage-close-all'));
    const restaurantId = getInventoryPracticeDataset()?.restaurantId ?? selectionToRestore?.restaurantId;
    if (snapshot && restaurantId) restoreSnapshot = { restaurantId, view: snapshot };
    endInventoryPracticeData();
    endPractice();
    if (snapshot)
        window.dispatchEvent(new CustomEvent('onboarding:inventory-restore-view', { detail: snapshot }));
    snapshot = null;
    customerSnapshot = null;
    const original = selectionToRestore;
    selectionToRestore = null;
    if (original && useAppUserDefaultStore.getState().restaurant?.id === original.restaurantId) {
        void useInventoryStore.getState().loadCountReview(original.restaurantId, original.selection);
    }
}
